import BusinessIcon from "@mui/icons-material/Business";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TableContainer from "@mui/material/TableContainer";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import {
  isUnauthorizedVendorRolloutBoardError,
  useGetVendorRolloutBoardQuery,
} from "legal-entities/vendor-rollouts/_api/vendor-rollout-board-api-service";
import { assembledAtLabel } from "legal-entities/vendor-rollouts/_views/vendor-rollout-board-presentation";
import VendorRolloutStatusTable from "legal-entities/vendor-rollouts/_views/vendor-rollout-status-table";

const BoardContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  width: "100%",
});
const AssembledAt = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  paddingTop: theme.spacing(1),
}));
/**
 * What the board shows in place of the table when there are no roll-outs.
 *
 * The app's shared answer for an empty table --
 * [`_shared/_views/tables/table-empty-state.tsx`](../../../_shared/_views/tables/table-empty-state.tsx),
 * described in [table empty states](../../../../../docs/software-design/table-empty-states.md) --
 * draws itself inside the cell Material React Table leaves where the rows would be, and reads the
 * search and filter state off the table instance it is handed. This board is a hand-built MUI table
 * with grouped stage headers rather than an MRT one, and it replaces the table outright rather than
 * filling it, so there is no instance to give that component and no filter state for it to read.
 */
const EmptyBoard = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  padding: theme.spacing(4, 2),
  textAlign: "center",
}));
const EmptyBoardIcon = styled("div")(({ theme }) => ({
  color: theme.palette.text.disabled,
  display: "flex",
  "& > .MuiSvgIcon-root": { fontSize: "2.5rem" },
}));
const EmptyBoardDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export type VendorRolloutStatusBoardProps = {
  /**
   * The client to report on. Omitted asks for every client at once, which is the staff question and
   * needs the platform grant — never a shorthand for the caller's own organization, which the API
   * refuses to infer. A client-facing surface always passes its own id.
   */
  readonly clientLegalEntityId?: Guid;
  /** What the empty state says. A client and a staff member are told different things. */
  readonly emptyDescription: string;
  readonly emptyTitle: string;
};

/**
 * The status board itself: one query, one table, and the three states around them.
 *
 * Both surfaces the roll-out has are this component with a different scope, because the staff tab
 * and the client's page are the same question asked by two kinds of caller. The API says the same
 * of its single endpoint, and a second component here would be a second place for the answer to
 * drift.
 */
export default function VendorRolloutStatusBoard(props: Readonly<VendorRolloutStatusBoardProps>) {
  const clientLegalEntityId = props.clientLegalEntityId;
  const boardQuery = useGetVendorRolloutBoardQuery({ clientLegalEntityId });

  if (boardQuery.isLoading) return <Loader text="Loading firm onboarding status..." />;

  if (boardQuery.error) {
    const refused = isUnauthorizedVendorRolloutBoardError(boardQuery.error);
    return (
      <Alert
        severity={refused ? "warning" : "error"}
        action={
          refused ? undefined : (
            <Button color="inherit" size="small" onClick={() => void boardQuery.refetch()}>
              Try again
            </Button>
          )
        }
      >
        {refused
          ? "You do not have permission to see these roll-outs."
          : "We could not load the firm onboarding status."}
      </Alert>
    );
  }

  const board = boardQuery.data;
  const rows = board?.rows ?? [];

  if (rows.length === 0) {
    return (
      <EmptyBoard role="status">
        <EmptyBoardIcon>
          <BusinessIcon />
        </EmptyBoardIcon>
        <Typography component="h2" variant="h6">
          {props.emptyTitle}
        </Typography>
        <EmptyBoardDescription variant="body2">{props.emptyDescription}</EmptyBoardDescription>
      </EmptyBoard>
    );
  }

  return (
    <BoardContainer>
      <TableContainer>
        <VendorRolloutStatusTable rows={rows} showClient={clientLegalEntityId === undefined} />
      </TableContainer>
      {/* A stage that has happened but whose outbox row has not drained yet reports as the stage
          before it, so the board says when it was read rather than implying it is instantaneous. */}
      <AssembledAt variant="caption">{board ? assembledAtLabel(board.assembledAt) : null}</AssembledAt>
    </BoardContainer>
  );
}
